import { isDefined } from "@clipboard-health/util-ts";
import type { IconType } from "@src/appV2/redesign/system/ui-components";
import type { IntentVariant } from "@src/appV2/redesign/system/ui-theme";
import { isAxiosError } from "axios";
import type { ReactNode } from "react";
import { useCallback, useMemo, useRef, useState } from "react";

import {
  type ClockInValidationResult,
  validateClockInTimestamp,
} from "../../MissedPunches/utils/validateClockInTimestamp";
import { getBottomSheetIntent } from "../TimekeepingAdjustmentBottomSheet";
import { useValidateTimekeepingAction } from "./useValidateTimekeepingAction";

export interface UseClockInTimeValidationParams {
  selectedTime: Date | undefined;
  shiftId: string;
  shiftStartAt: Date;
  shiftEndAt: Date;
  workplaceTimezone: string;
  geofenceArrivalTime?: Date;
  earliestPlausibleArrivalTime?: Date;
  workplaceSettings: {
    preventEarlyClockIns: boolean;
    earlyClockInMinutes?: number;
  };
  isEnforceGeofenceTimestampsEnabled: boolean;
  verifiedClockIn?: Date;
  workerClockIn?: Date;
  clockOutTime?: Date;
  breakStartTime?: Date;
  isGeofenceWorkplace?: boolean;
  isNfcOrMeshWorkplace?: boolean;
  /** When true, suppress the missed punch warning (TEM-2268 is always a missed punch). */
  suppressMissedPunchWarning?: boolean;
  /** When true, reject the selected time if it falls on the current minute. */
  preventCurrentTime?: boolean;
}

export interface UseClockInTimeValidationResult {
  isValid: boolean;
  intent?: IntentVariant;
  intentContent?: ReactNode;
  intentIcon?: IconType;
  validateOverlap: () => Promise<boolean>;
  isValidatingOverlap: boolean;
}

export function useClockInTimeValidation(
  params: UseClockInTimeValidationParams
): UseClockInTimeValidationResult {
  const {
    selectedTime,
    shiftId,
    shiftStartAt,
    shiftEndAt,
    workplaceTimezone,
    geofenceArrivalTime,
    earliestPlausibleArrivalTime,
    workplaceSettings,
    isEnforceGeofenceTimestampsEnabled,
    verifiedClockIn,
    workerClockIn,
    clockOutTime,
    breakStartTime,
    isGeofenceWorkplace,
    isNfcOrMeshWorkplace,
    suppressMissedPunchWarning = false,
    preventCurrentTime = false,
  } = params;

  const { mutateAsync: validateTimekeepingAction, isLoading: isValidatingOverlap } =
    useValidateTimekeepingAction();

  // useState so mutations trigger re-renders and memo recomputation
  const [overlapError, setOverlapError] = useState<string | undefined>(undefined);

  const localValidation: ClockInValidationResult | undefined = useMemo(() => {
    if (!isDefined(selectedTime)) {
      return undefined;
    }

    return validateClockInTimestamp({
      selectedTime,
      shiftStartAt,
      facilitySettings: workplaceSettings,
      workplaceTimezone,
      geofenceArrivalTime,
      earliestPlausibleArrivalTime,
      isEnforceGeofenceTimestampsEnabled,
      verifiedClockIn,
      workerClockIn,
      clockOutTime,
      breakStartTime,
      isGeofenceFacility: isGeofenceWorkplace,
      isNfcOrMeshFacility: isNfcOrMeshWorkplace,
      preventCurrentTime,
    });
  }, [
    selectedTime,
    shiftStartAt,
    workplaceSettings,
    workplaceTimezone,
    geofenceArrivalTime,
    earliestPlausibleArrivalTime,
    isEnforceGeofenceTimestampsEnabled,
    verifiedClockIn,
    workerClockIn,
    clockOutTime,
    breakStartTime,
    isGeofenceWorkplace,
    isNfcOrMeshWorkplace,
    preventCurrentTime,
  ]);

  // Clear overlap error when the user changes the selected time.
  // Uses the synchronous render-time pattern (React-recommended for storing
  // information from previous renders) to avoid a one-frame stale error flash.
  const selectedTimeMs = selectedTime?.getTime();
  const prevSelectedTimeMsRef = useRef(selectedTimeMs);
  if (prevSelectedTimeMsRef.current !== selectedTimeMs) {
    prevSelectedTimeMsRef.current = selectedTimeMs;
    if (isDefined(overlapError)) {
      setOverlapError(undefined);
    }
  }

  const validateOverlap = useCallback(async (): Promise<boolean> => {
    if (!isDefined(selectedTime) || !shiftId) {
      return false;
    }

    try {
      await validateTimekeepingAction({
        shiftId,
        editedClockIn: selectedTime.toISOString(),
        editedClockOut: shiftEndAt.toISOString(),
      });

      setOverlapError(undefined);
      return true;
    } catch (error: unknown) {
      // 400 means overlap detected — show the error message from the server
      if (isAxiosError(error) && error.response?.status === 400) {
        setOverlapError(
          (error.response.data as { message?: string })?.message ??
            "Clock-in time cannot overlap with another shift."
        );
        return false;
      }

      // Other errors (timeouts, 5xx, network) — block progress so the user can retry
      setOverlapError("Unable to validate overlap — please try again.");
      return false;
    }
  }, [selectedTime, shiftId, shiftEndAt, validateTimekeepingAction]);

  const intentProps = useMemo((): {
    intent?: IntentVariant;
    intentContent?: ReactNode;
    intentIcon?: IconType;
  } => {
    // Local error takes priority
    if (
      isDefined(localValidation) &&
      !localValidation.isValid &&
      isDefined(localValidation.message)
    ) {
      const { intent, intentIcon } = getBottomSheetIntent("error");
      return { intent, intentIcon, intentContent: localValidation.message };
    }

    // API overlap error
    if (isDefined(overlapError)) {
      const { intent, intentIcon } = getBottomSheetIntent("error");
      return { intent, intentIcon, intentContent: overlapError };
    }

    // Missed punch warning (blue info banner)
    if (
      !suppressMissedPunchWarning &&
      isDefined(localValidation) &&
      isDefined(localValidation.warningType) &&
      isDefined(localValidation.message)
    ) {
      const { intent, intentIcon } = getBottomSheetIntent("info");
      return { intent, intentIcon, intentContent: localValidation.message };
    }

    return {};
  }, [localValidation, overlapError, suppressMissedPunchWarning]);

  const isValid = isDefined(localValidation)
    ? localValidation.isValid && !isDefined(overlapError)
    : false;

  return {
    isValid,
    ...intentProps,
    validateOverlap,
    isValidatingOverlap,
  };
}
